
import React, { useState } from 'react';
import { safeSessionStorage } from '../utils/safeStorage';
import { Submission, MediaType, Participant } from '../types';
import { CameraIcon, VideoIcon, MicIcon, TypeIcon, StarIcon, CheckIcon, MonitorIcon, TriangleIcon } from './icons';
import { generatePlaceholderImage } from './imageUtils';
import ImageWithLoader from './ImageWithLoader';
import Avatar from './Avatar';
import { SignificationMediaDisplay } from './shared/media/SignificationMediaDisplay';
import { AnnotatedPhotoView } from './AnnotatedPhotoView';

const SESSION_CACHE_KEY = 'dekko_loaded_media_cache';

const getSessionCache = (): Set<string> => {
  try {
    const cached = safeSessionStorage.getItem(SESSION_CACHE_KEY);
    return cached ? new Set(JSON.parse(cached)) : new Set();
  } catch (e) {
    return new Set();
  }
};

const addToSessionCache = (url: string) => {
  try {
    const cache = getSessionCache();
    const normalized = normalizeUrl(url);
    if (!normalized) return;
    cache.add(normalized);
    safeSessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(Array.from(cache)));
  } catch (e) { }
};

const normalizeUrl = (url: string): string | null => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    return urlObj.origin + urlObj.pathname;
  } catch (e) {
    return url.split('?')[0];
  }
};

interface SubmissionCardProps {
  submission: Submission;
  participant: Participant;
  onSelect: (submission: Submission) => void;
  taskName?: string;
  // Mission Control Props
  isSelected?: boolean;
  onToggleSelection?: (id: string) => void;
  onToggleStar?: (id: string) => void;
  onToggleReview?: (id: string) => void;
  /** When set, shows a "Redo #N" pill at the thumbnail's bottom-left */
  redoAttempt?: number;
}

export const MediaIcon: React.FC<{ type: MediaType, className?: string }> = ({ type, className }) => {
  switch (type) {
    case MediaType.Photo:
      return <CameraIcon className={className} />;
    case MediaType.Video:
      return <VideoIcon className={className} />;
    case MediaType.Screen:
      return <MonitorIcon className={className} />;
    case MediaType.Audio:
      return <MicIcon className={className} />;
    case MediaType.Text:
      return <TypeIcon className={className} />;
    case MediaType.Signification:
      return <TriangleIcon className={className} />;
    default:
      return null;
  }
};

const SubmissionCard: React.FC<SubmissionCardProps> = ({
  submission,
  participant,
  onSelect,
  taskName,
  isSelected = false,
  onToggleSelection,
  onToggleStar,
  onToggleReview,
  redoAttempt
}) => {
  const { mediaUrls, mediaType, timestamp, isStarred, isReviewed } = submission;
  const { name } = participant;
  const [isVideoLoading, setIsVideoLoading] = useState(() => {
    const videoUrl = mediaUrls[0];
    if (typeof videoUrl === 'string') {
      const normalized = normalizeUrl(videoUrl);
      const cache = getSessionCache();
      if (normalized && cache.has(normalized)) return false;
    }
    return true;
  });

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleSelection) onToggleSelection(submission.id);
  };

  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleStar) onToggleStar(submission.id);
  };

  const handleReviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleReview) onToggleReview(submission.id);
  };

  const renderMediaPreview = () => {
    switch (mediaType) {
      case MediaType.Photo:
        const imageUrl = mediaUrls[0] || generatePlaceholderImage(500, 500);
        if (submission.photoAnnotations?.[0]) {
          return <AnnotatedPhotoView imageUrl={imageUrl} annotation={submission.photoAnnotations[0]} className="w-full h-full" compact />;
        }
        return <ImageWithLoader src={imageUrl} alt={`Submission from ${name}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />;
      case MediaType.Video:
      case MediaType.Screen:
        const videoUrl = mediaUrls[0];
        if (!videoUrl) {
          return (
            <div className="w-full h-full bg-status-info-bg flex items-center justify-center">
              {mediaType === MediaType.Screen ? <MonitorIcon className="w-8 h-8 text-status-info" /> : <VideoIcon className="w-8 h-8 text-status-info" />}
            </div>
          );
        }
        return (
          <div className="w-full h-full relative overflow-hidden">
            {isVideoLoading && (
              <div className="absolute inset-0 shimmer-bg z-10" />
            )}
            <video
              src={`${videoUrl}#t=10`}
              className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${isVideoLoading ? 'opacity-0' : 'opacity-100'}`}
              muted
              playsInline
              preload="metadata"
              onLoadedData={() => {
                setIsVideoLoading(false);
                const videoUrl = mediaUrls[0];
                if (videoUrl) addToSessionCache(videoUrl);
              }}
            />
          </div>
        );
      case MediaType.Audio:
        return (
          <div className="w-full h-full bg-app-bg flex items-center justify-center">
            <MicIcon className="w-8 h-8 text-text-faint" />
          </div>
        );
      case MediaType.Text:
        return (
          <div className="w-full h-full bg-status-info-bg flex items-center justify-center">
            <TypeIcon className="w-8 h-8 text-status-info" />
          </div>
        );
      case MediaType.Signification:
        return <SignificationMediaDisplay submission={submission} compact />;
    }
  }

  return (
    <div
      className={`relative bg-white rounded-xl border border-app-border shadow-sm overflow-hidden group cursor-pointer transition-all duration-300 ${isSelected
        ? 'ring-4 ring-action/40 shadow-lg transform scale-[1.02]'
        : 'hover:shadow-md'
        }`}
      onClick={() => onSelect(submission)}
    >
      {/* Selection Checkbox (Visible on hover or selected) */}
      <div
        className={`absolute top-3 left-3 z-10 transition-opacity duration-200 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
        onClick={handleCheckboxClick}
      >
        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-action border-action' : 'bg-white border-app-border hover:border-action/40'
          }`}>
          {isSelected && <CheckIcon className="w-4 h-4 text-white" />}
        </div>
      </div>

      <div className="aspect-square relative overflow-hidden bg-app-bg">
        {renderMediaPreview()}

        {/* Redo attempt pill — thumbnail bottom-left (kept clear of the
            bottom-right review toggle and the card's meta row below) */}
        {redoAttempt !== undefined && (
          <div className="absolute bottom-3 left-3 z-10" style={{ background: '#D4715A', color: 'white', fontSize: 9, fontWeight: 700, borderRadius: 6, padding: '2px 6px' }}>
            Redo #{redoAttempt}
          </div>
        )}

        {/* Media Type Icon */}
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur text-action rounded-full p-2 shadow-sm">
          <MediaIcon type={mediaType} className="w-4 h-4" />
        </div>

        {/* Reviewed Status Overlay / Toggle */}
        {isReviewed ? (
          <button
            onClick={handleReviewClick}
            title="Reviewed"
            className="absolute flex items-center justify-center transition-all"
            style={{
              bottom: 8,
              right: 8,
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: '#6E7A5A',
              border: 'none',
            }}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20,6 9,17 4,12"/></svg>
          </button>
        ) : (
          <button
            onClick={handleReviewClick}
            title="Review"
            aria-label="Mark as reviewed"
            className="absolute flex items-center justify-center transition-colors hover:bg-white"
            style={{
              bottom: 8,
              right: 8,
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.85)',
              border: '1px solid #E8E4DF',
              backdropFilter: 'blur(4px)',
            }}
          >
            <CheckIcon className="w-3 h-3" style={{ color: '#9A9490' }} />
          </button>
        )}
      </div>

      <div className="p-3 relative">
        {/* Star Action */}
        <button
          className={`absolute top-3 right-3 p-1 rounded-full transition-colors ${isStarred ? 'text-status-warn' : 'text-text-faint hover:text-status-warn'}`}
          onClick={handleStarClick}
        >
          <StarIcon className={`w-4 h-4 ${isStarred ? 'fill-current' : ''}`} />
        </button>

        {taskName && (
          <p className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-0.5 truncate pr-5">{taskName}</p>
        )}
        <p className="text-sm font-bold text-text-ink truncate mb-1 pr-5">{name}</p>
        <div className="flex items-center">
          <Avatar name={participant.name} avatarUrl={participant.avatarUrl} id={participant.id} className="w-5 h-5 rounded-full mr-1.5 object-cover ring-2 ring-white" />
          <p className="text-[10px] font-semibold text-text-muted">{new Date(timestamp).toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
};

export default SubmissionCard;
